import React, { useEffect, useMemo, useState } from "react";
import MUIAppBar from "@mui/material/AppBar";
import MUIToolbar from "@mui/material/Toolbar";
import makeStyles from "@mui/styles/makeStyles";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { Link } from "react-router-dom";
import clsx from "clsx";
import Box from "@mui/material/Box";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useShoppingCart } from "../../providers/ShoppingCartProvider";
import { usePortal } from "../../../Providers/PortalProvider";
import { formatCurrency } from "../../../utils";
import { useTranslation } from "react-i18next";
import MainMenu from "./MainMenu";
import { usePictureSets } from "../../hooks/usePictureSets";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    flexShrink: 0,
  },
  logoContainer: {
    flexGrow: 1,
    flexShrink: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "70px",
    "& > a > img": {
      maxWidth: "100%",
      maxHeight: "50px",
    },
  },
  cartItems: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "20px",
    height: "20px",
    borderRadius: "10px",
    color: theme.palette.secondary.contrastText,
    backgroundColor: theme.palette.secondary.main,
    fontSize: "12px",
    lineHeight: "20px",
    textAlign: "center",
  },
  animate: {
    animationName: "$animateQuantity",
    animationDuration: "1s",
  },
  "@keyframes animateQuantity": {
    "0%": {},
    "50%": {
      transform: "scale(1.3)",
    },
    "100%": {},
  },

  promoteBar: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    padding: theme.spacing(1),
    textAlign: "center",
    fontSize: "12px",
  },

  // Applied alongside the rules above rather than expressed as a
  // props-dependent margin, so that the sheet stays static: a rule with a
  // function value gets one linked sheet per mounted instance, and those
  // cannot share a class name, so the class VWO targets would change on every
  // remount - see components/generateClassName.
  bottomMargin: {
    marginBottom: theme.spacing(2),
  },
  noBottomMargin: {
    marginBottom: 0,
  },
}));

const Toolbar = (props) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const { currentPictureSet } = usePictureSets();
  const portal = usePortal();

  const logoPath =
    currentPictureSet?.photographer?.logo?.url || window.PAGE_SETTINGS?.logo;

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // inert means that for the height holder, we don't get tab focus (Accessability)
  return (
    <MUIToolbar
      className={clsx(
        classes.toolbar,
        props.disableBottomMargin
          ? classes.noBottomMargin
          : classes.bottomMargin,
      )}
      {...(props.isHeightHolder ? { inert: "true" } : {})}
    >
      <IconButton
        className={classes.menuButton}
        color="primary"
        aria-label="open drawer"
        onClick={() => setMenuOpen(true)}
        data-testid="open-drawer-menu-button"
      >
        <MenuIcon />
      </IconButton>

      <div className={classes.logoContainer}>
        <Link to="/">
          {logoPath && (
            <img className={classes.logo} src={logoPath} alt={portal.name} />
          )}
        </Link>
      </div>

      <Box>
        <ShoppingCart overrideQuantity={props.overrideQuantity} />
      </Box>
      <MainMenu menuOpen={menuOpen} onClose={closeMenu} />
    </MUIToolbar>
  );
};

const AppBar = (props) => {
  const { webshop } = usePortal();
  const { loading } = useShoppingCart();

  let disableBottomMargin = false;
  if (
    !loading &&
    (webshop.freeShippingThreshold >= 0 ||
      props.dangerousExtraPromoText ||
      props.disableBottomMargin)
  ) {
    disableBottomMargin = true;
  }

  return (
    <React.Fragment>
      <MUIAppBar sx={{ color: "black", backgroundColor: "white" }}>
        <Toolbar
          disableBottomMargin={true}
          overrideQuantity={props.overrideQuantity}
        />
      </MUIAppBar>
      <Toolbar
        disableBottomMargin={disableBottomMargin}
        overrideQuantity={props.overrideQuantity}
        isHeightHolder
      />

      <PromoteBar
        overrideTotalPrice={props.overrideTotalPrice}
        dangerousExtraPromoText={props.dangerousExtraPromoText}
        disableBottomMargin={props.disableBottomMargin}
      />
    </React.Fragment>
  );
};

const PromoteBar = (props) => {
  const { dangerousExtraPromoText } = props;
  const { webshop } = usePortal();
  const { totalPrice: shoppingCartTotalPrice, loading } = useShoppingCart();
  const { t } = useTranslation();
  const classes = useStyles();

  let totalPrice = shoppingCartTotalPrice;
  if (props.overrideTotalPrice) {
    totalPrice = props.overrideTotalPrice;
  }

  let freeShippingText = null;
  if (webshop.freeShippingThreshold >= 0 && !loading) {
    let text = t("Free delivery!");
    if (totalPrice <= 0) {
      text = t("Free delivery when you spend over %s", {
        postProcess: "sprintf",
        sprintf: [formatCurrency(webshop.freeShippingThreshold)],
      });
    } else if (totalPrice < webshop.freeShippingThreshold) {
      text = t("Shop products for %s more and get free delivery!", {
        postProcess: "sprintf",
        sprintf: [formatCurrency(webshop.freeShippingThreshold - totalPrice)],
      });
    }
    freeShippingText = text;
  }

  if (!dangerousExtraPromoText && !freeShippingText) {
    return null;
  }

  return (
    <div
      className={clsx(
        classes.promoteBar,
        props.disableBottomMargin
          ? classes.noBottomMargin
          : classes.bottomMargin,
      )}
    >
      {freeShippingText && <div>{freeShippingText}</div>}
      {dangerousExtraPromoText && (
        <div dangerouslySetInnerHTML={dangerousExtraPromoText} />
      )}
    </div>
  );
};

const getNavigationUrl = () => {
  return "/shopping-cart";
};

const ShoppingCart = (props) => {
  const classes = useStyles();
  const { items: shoppingCartItems } = useShoppingCart();
  let numItems = useMemo(() => {
    return shoppingCartItems.reduce((items, item) => {
      return items + item.quantity;
    }, 0);
  }, [shoppingCartItems]);

  if (props.overrideQuantity) {
    numItems = props.overrideQuantity;
  }

  return (
    <IconButton
      className={classes.menuButton}
      color="primary"
      aria-label="open drawer"
      component={Link}
      to={getNavigationUrl("SHOPPING_CART")}
    >
      <ShoppingCartIcon />
      <ShoppingCartItemCount itemCount={numItems} />
    </IconButton>
  );
};

const ShoppingCartItemCount = ({ itemCount }) => {
  const classes = useStyles();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    // Animate on change:
    setAnimate(true);
    setTimeout(() => {
      setAnimate(false);
    }, 1000);
  }, [itemCount]);

  if (itemCount == 0) {
    return null;
  }

  return (
    <div className={`${classes.cartItems} ${animate ? classes.animate : ""}`}>
      {itemCount}
    </div>
  );
};

export default AppBar;
